import type { PlanDto, PlanWarningDto } from '@room-mates/shared';
import { type ReactElement, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { formatDate, formatDateTime } from '../../shared/format.js';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  ErrorAlert,
  LoadingBlock,
  PageHeader,
} from '../../shared/ui/index.js';
import { useGeneratePlan, usePlan, useSojourn } from '../sojourns/sojournHooks.js';
import { PlanSegmentView } from './PlanSegmentView.js';

const WARNING_TONE = {
  error: 'error',
  warning: 'warning',
  info: 'info',
} as const;

export function PlanPage(): ReactElement {
  const t = useTranslation();
  const { sojournId = '' } = useParams();
  const sojourn = useSojourn(sojournId);
  const plan = usePlan(sojournId);
  const generate = useGeneratePlan(sojournId);
  const [segmentIndex, setSegmentIndex] = useState(0);
  const [shareDoubleBeds, setShareDoubleBeds] = useState(true);

  if (sojourn.isPending || plan.isPending) {
    return <LoadingBlock />;
  }

  if (sojourn.data === undefined) {
    return (
      <div className="stack">
        <ErrorAlert error={sojourn.error} />
        <Link to="/sojourns">{t.planPage.backToSojourns}</Link>
      </div>
    );
  }

  const run = (): void => {
    setSegmentIndex(0);
    generate.mutate({ allowSharedDoubleBeds: shareDoubleBeds });
  };

  return (
    <div className="stack stack--loose">
      <PageHeader
        title={t.planPage.titleFor(sojourn.data.name)}
        subtitle={
          plan.data === null || plan.data === undefined ? (
            t.planPage.noPlanYetFor(
              formatDate(sojourn.data.startDate),
              formatDate(sojourn.data.endDate),
            )
          ) : (
            <>{t.planPage.computedAt(formatDateTime(plan.data.generatedAt))}</>
          )
        }
        actions={
          <>
            <Link to={`/sojourns/${sojournId}`}>
              <Button variant="secondary">{t.planPage.editTheSojourn}</Button>
            </Link>
            <Button onClick={run} loading={generate.isPending}>
              {plan.data === null || plan.data === undefined
                ? t.planPage.generateThePlan
                : t.planPage.regenerate}
            </Button>
          </>
        }
      />

      <ErrorAlert error={plan.error ?? generate.error} />

      <Card flat>
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={shareDoubleBeds}
            onChange={(event) => setShareDoubleBeds(event.target.checked)}
          />
          {t.planPage.shareDoubleBedsLabel}
        </label>
      </Card>

      {plan.data === null || plan.data === undefined ? (
        <EmptyState
          title={t.planPage.noPlanTitle}
          description={t.planPage.noPlanDescription}
          action={
            <Button onClick={run} loading={generate.isPending}>
              {t.planPage.generateThePlan}
            </Button>
          }
        />
      ) : (
        <PlanResult
          plan={plan.data}
          segmentIndex={segmentIndex}
          onSelectSegment={setSegmentIndex}
        />
      )}
    </div>
  );
}

function PlanResult({
  plan,
  segmentIndex,
  onSelectSegment,
}: {
  plan: PlanDto;
  segmentIndex: number;
  onSelectSegment: (index: number) => void;
}): ReactElement {
  const t = useTranslation();
  const segment = plan.segments[Math.min(segmentIndex, plan.segments.length - 1)];

  return (
    <div className="stack stack--loose">
      <PlanStats plan={plan} />
      <PlanWarnings warnings={plan.warnings} />

      {plan.segments.length === 0 ? (
        <EmptyState
          title={t.planPage.nothingToArrangeTitle}
          description={t.planPage.nothingToArrangeDescription}
        />
      ) : null}

      {plan.segments.length > 1 ? (
        <Card flat>
          <div className="stack stack--tight">
            <strong>{t.planPage.periods}</strong>
            <p className="card__meta">{t.planPage.periodsHint}</p>
            <div className="option-list">
              {plan.segments.map((candidate, index) => (
                <button
                  key={candidate.index}
                  type="button"
                  className={`option-chip${index === segmentIndex ? ' option-chip--selected' : ''}`}
                  aria-pressed={index === segmentIndex}
                  onClick={() => onSelectSegment(index)}
                >
                  {formatDate(candidate.start)} → {formatDate(candidate.end)} (
                  {t.count.nights(candidate.nights)})
                </button>
              ))}
            </div>
          </div>
        </Card>
      ) : null}

      {segment !== undefined ? <PlanSegmentView plan={plan} segment={segment} /> : null}
    </div>
  );
}

function PlanStats({ plan }: { plan: PlanDto }): ReactElement {
  const t = useTranslation();
  const housed =
    plan.stats.guestNights === 0
      ? 100
      : Math.round((plan.stats.assignedGuestNights / plan.stats.guestNights) * 100);

  return (
    <div className="stat-row">
      <Stat value={`${housed}%`} label={t.planPage.ofGuestNightsHoused} />
      <Stat value={String(plan.stats.segmentCount)} label={t.planPage.periodsLabel} />
      <Stat
        value={`${Math.round(plan.stats.occupancyRate * 100)}%`}
        label={t.planPage.bedOccupancy}
      />
      <Stat value={String(plan.stats.relocationCount)} label={t.planPage.midStayMoves} />
      <Stat value={String(Math.round(plan.score.total))} label={t.planPage.preferenceScore} />
      <Stat value={`${plan.stats.computeMs} ms`} label={t.planPage.toCompute} />
    </div>
  );
}

function Stat({ value, label }: { value: string; label: string }): ReactElement {
  return (
    <div className="stat">
      <div className="stat__value">{value}</div>
      <div className="stat__label">{label}</div>
    </div>
  );
}

function PlanWarnings({ warnings }: { warnings: readonly PlanWarningDto[] }): ReactElement | null {
  if (warnings.length === 0) {
    return null;
  }
  return (
    <div className="stack stack--tight">
      {warnings.map((warning, index) => (
        <Alert key={`${warning.code}-${index}`} tone={WARNING_TONE[warning.severity]}>
          {warning.message}
        </Alert>
      ))}
    </div>
  );
}
